import { useCallback, useMemo, useState } from 'react';
import { HelpCircle } from 'lucide-react';

import {
  Button,
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Alert,
  AlertDescription,
} from '@design-system';
import { type D2CPaymentTab, D2C_PAYMENT_TAB_LABELS } from '@shared/guidedPlanningPreferences';
import { cn } from '@client/lib';

import { QuoteCard } from '@/features/guided-financial-planning/components/quote-card';
import { InlinePremiumSlider } from '@/features/guided-financial-planning/components/inline-premium-slider';
import { RequestHelpDialog } from '@/features/guided-financial-planning/components/request-help-dialog';
import {
  type GuidedPlanningQuote,
  getQuoteId,
  getPremiumAmount,
} from '@/features/guided-financial-planning/hooks';
import type { PaymentTabData } from '@/features/guided-financial-planning/hooks/use-quotes-by-payment-tab';
import {
  type PerTabPremiumApi,
  getRoiMetricForTab,
} from '@/features/guided-financial-planning/hooks/use-per-tab-premium-state';
import type { CoverageType } from '@/features/guided-financial-planning/hooks/use-coverage-type-toggle';

interface CoverageOption {
  value: CoverageType;
  label: string;
}

interface QuotesTabViewProps {
  tabs: PaymentTabData[];
  defaultTab: D2CPaymentTab | undefined;
  isSessionComplete: boolean;
  hasSelectedQuote: boolean;
  isQuoteAdded: (quote: GuidedPlanningQuote) => boolean;
  onSelectQuote: (quote: GuidedPlanningQuote) => void;
  onRemoveQuote: (quote: GuidedPlanningQuote) => void;
  perTabPremium: PerTabPremiumApi;
  showHelpAlert?: boolean;
  coverageOptions?: CoverageOption[];
  selectedCoverageType?: CoverageType;
  onCoverageTypeChange?: (value: string) => void;
}

const SUPPORT_EMAIL = 'support@wealthassurancegroup.com';

function EmptyTabContent({ tab, isD2C }: { tab: D2CPaymentTab; isD2C: boolean }) {
  const label = D2C_PAYMENT_TAB_LABELS[tab].toLowerCase();

  return (
    <Empty className="bg-muted/50 text-wrap rounded-xl border border-dashed">
      <EmptyHeader className="max-w-2xl">
        <EmptyTitle className="text-gray-950">No {label} options available</EmptyTitle>
        <EmptyDescription className="text-foreground">
          {isD2C
            ? `We don't have any ${label} options for you right now. Send an email describing what you're looking for to connect with a licensed agent, review your options, and receive quotes. Licensed support is provided through Wealth Assurance Group, a Waterlily company.`
            : `There are no ${label} options available for this client's profile. Try switching to a different payment type.`}
        </EmptyDescription>
        {isD2C && (
          <div className="bg-muted mt-3 rounded-lg border p-4 text-center">
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Help finding insurance options`}
              className="text-primary text-lg font-semibold hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
        )}
      </EmptyHeader>
    </Empty>
  );
}

const INITIAL_VISIBLE_COUNT = 4;

export function QuotesTabView({
  tabs,
  defaultTab,
  isSessionComplete,
  hasSelectedQuote,
  isQuoteAdded,
  onSelectQuote,
  onRemoveQuote,
  perTabPremium,
  showHelpAlert = false,
  coverageOptions,
  selectedCoverageType,
  onCoverageTypeChange,
}: QuotesTabViewProps) {
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [expandedTabs, setExpandedTabs] = useState<Set<D2CPaymentTab>>(new Set());
  const activeDefault = defaultTab ?? 'monthly';
  const [activeTab, setActiveTab] = useState<D2CPaymentTab>(activeDefault);
  const activeTabPremiumState = perTabPremium.getTabState(activeTab);

  const showCoverageSelect = coverageOptions && coverageOptions.length > 1 && onCoverageTypeChange;

  const activeTabData = useMemo(() => tabs.find(t => t.tab === activeTab), [tabs, activeTab]);

  const toggleExpanded = useCallback((tab: D2CPaymentTab) => {
    setExpandedTabs(prev => {
      const next = new Set(prev);
      if (next.has(tab)) {
        next.delete(tab);
      } else {
        next.add(tab);
      }
      return next;
    });
  }, []);

  // Compute quotes for the active tab
  const { visibleQuotes, hiddenCount, sortedQuotes, rawQuotes } = useMemo(() => {
    const raw = activeTabData?.quotes ?? [];
    const tabPremiumState = perTabPremium.getTabState(activeTab);
    const adjustedQuotes = tabPremiumState ? perTabPremium.getAdjustedQuotes(activeTab) : raw;

    const sorted = [...adjustedQuotes].sort((a, b) => {
      const premiumDiff = getPremiumAmount(a) - getPremiumAmount(b);
      if (premiumDiff !== 0) return premiumDiff;
      return (b.roi ?? 0) - (a.roi ?? 0);
    });

    const isExpanded = expandedTabs.has(activeTab);
    const visible = isExpanded ? sorted : sorted.slice(0, INITIAL_VISIBLE_COUNT);
    const hidden = Math.max(0, sorted.length - INITIAL_VISIBLE_COUNT);

    return { visibleQuotes: visible, hiddenCount: hidden, sortedQuotes: sorted, rawQuotes: raw };
  }, [activeTabData, activeTab, perTabPremium, expandedTabs]);

  return (
    <div className="space-y-4">
      {showHelpAlert && !isSessionComplete && (
        <Alert
          variant="info"
          className="flex! items-start gap-6 rounded-xl p-3 md:items-center"
        >
          <div className="flex items-start gap-2">
            <HelpCircle className="size-4! md:size-4.5! translate-y-0.5! shrink-0" />
            <AlertDescription className="text-primary-dark font-medium md:text-base">
              Need help finding the right plan? Speak with a licensed agent to review options and
              get quotes.
            </AlertDescription>
          </div>
          <Button
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => setShowHelpDialog(true)}
          >
            Get Help
          </Button>
        </Alert>
      )}

      {/* Filter bar: [Payment ▾] [Coverage ▾] [Budget slider] */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="flex items-center gap-2">
          <Select
            value={activeTab}
            onValueChange={v => {
              setActiveTab(v as D2CPaymentTab);
              setExpandedTabs(new Set());
            }}
          >
            <SelectTrigger
              size="sm"
              className="w-max"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabs.map(({ tab, quotes }) => (
                <SelectItem
                  key={tab}
                  value={tab}
                >
                  {D2C_PAYMENT_TAB_LABELS[tab]}
                  <span className="text-muted-foreground text-xs">({quotes.length})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {showCoverageSelect && (
            <Select
              value={selectedCoverageType}
              onValueChange={onCoverageTypeChange}
            >
              <SelectTrigger
                size="sm"
                className="w-max"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {coverageOptions.map(opt => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {activeTabPremiumState && !isSessionComplete && (
          <InlinePremiumSlider
            value={activeTabPremiumState.value}
            min={activeTabPremiumState.bounds.min}
            max={activeTabPremiumState.bounds.max}
            step={activeTabPremiumState.bounds.step}
            onValueChange={value => perTabPremium.setTabPremium(activeTab, value)}
            showRange={activeTab === 'monthly' || activeTab === 'annual'}
          />
        )}
      </div>

      {/* Quote cards for active tab */}
      <div>
        {rawQuotes.length === 0 ? (
          <EmptyTabContent
            tab={activeTab}
            isD2C={showHelpAlert}
          />
        ) : (
          <div className="space-y-4">
            {sortedQuotes.length === 0 ? (
              <Empty className="bg-muted rounded-xl border border-dashed">
                <EmptyHeader>
                  <EmptyTitle className="text-gray-950">No options at this budget</EmptyTitle>
                  <EmptyDescription className="text-foreground">
                    Try increasing the budget slider to see more options.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <>
                <div
                  className={cn(
                    'grid gap-4',
                    visibleQuotes.length === 1
                      ? 'mx-auto max-w-md grid-cols-1'
                      : 'grid-cols-1 md:grid-cols-2',
                  )}
                >
                  {visibleQuotes.map(quote => {
                    const isAdded = isQuoteAdded(quote);
                    const isBlockedBySelection = hasSelectedQuote && !isAdded;

                    return (
                      <QuoteCard
                        key={getQuoteId(quote)}
                        quote={quote}
                        onSelect={onSelectQuote}
                        onRemove={onRemoveQuote}
                        isAlreadyAdded={isAdded}
                        disabled={isSessionComplete || isBlockedBySelection}
                        showSelectionLimitTooltip={isBlockedBySelection}
                        roiMetric={getRoiMetricForTab(activeTab)}
                      />
                    );
                  })}
                </div>

                {hiddenCount > 0 && (
                  <div className="flex justify-center">
                    <Button
                      variant="link"
                      onClick={() => toggleExpanded(activeTab)}
                    >
                      {expandedTabs.has(activeTab)
                        ? 'Show less'
                        : `Show ${hiddenCount} more option${hiddenCount === 1 ? '' : 's'}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <RequestHelpDialog
        open={showHelpDialog}
        onOpenChange={setShowHelpDialog}
      />
    </div>
  );
}
